import React, { useRef, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import {
  Bold, Italic, Underline, Strikethrough,
  Link, List, ListOrdered, AlignLeft, AlignCenter, AlignRight
} from 'lucide-react';

const fontSizes = [
  { label: 'Малък', value: '2' },
  { label: 'Нормален', value: '3' },
  { label: 'Голям', value: '5' },
  { label: 'Много голям', value: '7' },
];

export default function RichTextEditor({ value, onChange, placeholder = 'Въведете текст...' }) {
  const editorRef = useRef(null);
  const isInit = useRef(false);

  useEffect(() => {
    if (!isInit.current && editorRef.current) {
      editorRef.current.innerHTML = DOMPurify.sanitize(value || '');
      isInit.current = true;
    }
  }, [value]);

  const exec = useCallback((command, val = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const handleHeading = useCallback((tag) => {
    editorRef.current?.focus();
    document.execCommand('formatBlock', false, tag);
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const handleLink = useCallback(() => {
    const sel = window.getSelection();
    const range = sel?.rangeCount > 0 ? sel.getRangeAt(0) : null;
    const url = window.prompt('Въведете URL:');
    if (url && range) {
      sel.removeAllRanges();
      sel.addRange(range);
      exec('createLink', url.startsWith('http') ? url : 'https://' + url);
    }
  }, [exec]);

  const handleInput = () => onChange(editorRef.current?.innerHTML || '');

  const handleKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'b') { e.preventDefault(); exec('bold'); }
      if (e.key === 'i') { e.preventDefault(); exec('italic'); }
      if (e.key === 'u') { e.preventDefault(); exec('underline'); }
    }
  };

  const Divider = () => <div className="w-px h-5 bg-slate-200 mx-0.5 self-center flex-shrink-0" />;

  const Btn = ({ cmd, val, title, children, onClick }) => (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        if (onClick) onClick();
        else exec(cmd, val);
      }}
      title={title}
      className="p-1.5 rounded transition-all duration-150 text-slate-500 hover:bg-[#00966E]/10 hover:text-[#00966E] flex-shrink-0"
    >
      {children}
    </button>
  );

  const HeadingBtn = ({ tag, label }) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); handleHeading(tag); }}
      title={`Заглавие ${label}`}
      className="px-1.5 py-1 rounded text-xs font-bold text-slate-500 hover:bg-[#00966E]/10 hover:text-[#00966E] transition-all flex-shrink-0"
    >
      {label}
    </button>
  );

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-[#00966E]/30 focus-within:border-[#00966E] transition-all shadow-sm bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-100 bg-slate-50/80 min-h-[38px]">

        {/* Text style */}
        <Btn cmd="bold" title="Удебелен (Ctrl+B)"><Bold className="h-3.5 w-3.5" /></Btn>
        <Btn cmd="italic" title="Курсив (Ctrl+I)"><Italic className="h-3.5 w-3.5" /></Btn>
        <Btn cmd="underline" title="Подчертан (Ctrl+U)"><Underline className="h-3.5 w-3.5" /></Btn>
        <Btn cmd="strikeThrough" title="Зачертан"><Strikethrough className="h-3.5 w-3.5" /></Btn>

        <Divider />

        {/* Headings */}
        <HeadingBtn tag="h2" label="H1" />
        <HeadingBtn tag="h3" label="H2" />
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); handleHeading('p'); }}
          title="Нормален текст"
          className="px-1.5 py-1 rounded text-xs text-slate-500 hover:bg-[#00966E]/10 hover:text-[#00966E] transition-all flex-shrink-0"
        >¶</button>

        <Divider />

        {/* Lists */}
        <Btn cmd="insertUnorderedList" title="Списък с точки"><List className="h-3.5 w-3.5" /></Btn>
        <Btn cmd="insertOrderedList" title="Номериран списък"><ListOrdered className="h-3.5 w-3.5" /></Btn>

        <Divider />

        {/* Alignment */}
        <Btn cmd="justifyLeft" title="Ляво"><AlignLeft className="h-3.5 w-3.5" /></Btn>
        <Btn cmd="justifyCenter" title="Центрирано"><AlignCenter className="h-3.5 w-3.5" /></Btn>
        <Btn cmd="justifyRight" title="Дясно"><AlignRight className="h-3.5 w-3.5" /></Btn>

        <Divider />

        {/* Font size */}
        <select
          defaultValue="3"
          onChange={(e) => exec('fontSize', e.target.value)}
          onMouseDown={(e) => e.stopPropagation()}
          className="text-xs border border-slate-200 rounded-md bg-white text-slate-600 px-1.5 py-0.5 cursor-pointer focus:ring-0 focus:outline-none hover:border-slate-300 transition-colors flex-shrink-0"
        >
          {fontSizes.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        <Divider />

        {/* Link */}
        <Btn title="Добави линк" onClick={handleLink}><Link className="h-3.5 w-3.5" /></Btn>
      </div>

      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
        className="min-h-[140px] max-h-[360px] overflow-y-auto px-4 py-3 text-sm text-slate-900 focus:outline-none leading-relaxed
          [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-3 [&_h2]:mb-1
          [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-slate-700 [&_h3]:mt-2 [&_h3]:mb-0.5
          [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1 [&_ul_li]:my-0.5
          [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1 [&_ol_li]:my-0.5
          [&_a]:text-[#00966E] [&_a]:underline [&_a]:cursor-pointer
          [&_blockquote]:border-l-4 [&_blockquote]:border-[#00966E]/30 [&_blockquote]:pl-3 [&_blockquote]:text-slate-600
          empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400 empty:before:pointer-events-none"
      />
    </div>
  );
}
